import React, { createContext, useContext, useMemo, useState } from 'react';
import { GripVertical } from 'lucide-react';
import { isDescendantPath, pathKey } from './criteriaUtils';

/**
 * Drag state for the criteria tree. The tree renders itself recursively, so the drag
 * source and every drop target share this instead of threading handlers through each level.
 *
 * Three drop targets, kept deliberately distinct so no two overlap on the same pixels:
 * a gap between children moves the node to that position, a condition row pairs the two
 * into a new group, and a group header moves the node inside that group.
 */
const CriteriaDragContext = createContext(null);

export function useCriteriaDrag() {
  return useContext(CriteriaDragContext);
}

export function CriteriaDragProvider({ onMove, onGroup, children }) {
  const [draggedPath, setDraggedPath] = useState(null);

  const value = useMemo(
    () => ({
      draggedPath,
      isDragging: draggedPath != null,
      startDrag: setDraggedPath,
      endDrag: () => setDraggedPath(null),
      /** True when the dragged node may land inside the group at `groupPath`. */
      canDropInto: (groupPath) => draggedPath != null && !isDescendantPath(groupPath, draggedPath),
      /**
       * True when dropping at this exact position would actually move something. The two
       * gaps either side of the dragged node lead back to where it already is, and showing
       * them as targets makes a working drag look broken.
       */
      canDropAt: (parentPath, index) => {
        if (draggedPath == null || isDescendantPath(parentPath, draggedPath)) return false;
        const fromParent = draggedPath.slice(0, -1);
        const sameParent =
          fromParent.length === parentPath.length && fromParent.every((v, i) => v === parentPath[i]);
        if (!sameParent) return true;
        const fromIdx = draggedPath[draggedPath.length - 1];
        return index !== fromIdx && index !== fromIdx + 1;
      },
      /** True when the dragged node may be paired with the node at `path` into a new group. */
      canPairWith: (path) =>
        draggedPath != null &&
        pathKey(path) !== pathKey(draggedPath) &&
        !isDescendantPath(path, draggedPath) &&
        !isDescendantPath(draggedPath, path),
      moveTo: (parentPath, index) => {
        if (draggedPath != null) onMove(draggedPath, parentPath, index);
        setDraggedPath(null);
      },
      pairWith: (targetPath) => {
        if (draggedPath != null) onGroup(targetPath, draggedPath);
        setDraggedPath(null);
      },
    }),
    [draggedPath, onMove, onGroup]
  );

  return <CriteriaDragContext.Provider value={value}>{children}</CriteriaDragContext.Provider>;
}

/** Grab handle. Dragging the whole row would swallow text selection in its inputs. */
export const DragHandle = ({ path, label = 'Drag to move' }) => {
  const drag = useCriteriaDrag();
  if (!drag) return null;
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.stopPropagation();
        // Firefox refuses to start a drag without payload, even when it goes unused.
        e.dataTransfer.setData('text/plain', pathKey(path));
        e.dataTransfer.effectAllowed = 'move';
        // Revealing the landing strips reflows the rows around the handle. Doing that
        // while dragstart is still running makes Chrome abandon the drag it just began,
        // so the browser gets its drag image from a stable page and the strips arrive
        // on the next frame.
        requestAnimationFrame(() => drag.startDrag(path));
      }}
      onDragEnd={drag.endDrag}
      className="shrink-0 cursor-grab rounded p-1 text-zinc-400 hover:bg-[#E8EEF5] hover:text-zinc-700 active:cursor-grabbing"
      title="Drag to move, or drop onto another condition to group them"
      aria-label={label}
    >
      <GripVertical className="h-4 w-4" />
    </span>
  );
};

/** Shared drag-over bookkeeping: returns handlers plus whether this target is hovered. */
function useDropTarget(active, onDrop) {
  const [over, setOver] = useState(false);
  if (!active) return { over: false, handlers: {} };
  return {
    over,
    handlers: {
      onDragOver: (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (!over) setOver(true);
      },
      onDragLeave: () => setOver(false),
      onDrop: (e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        onDrop();
      },
    },
  };
}

/**
 * Landing strip between two children. Hidden until a drag is in flight so it never
 * adds spacing to a tree at rest.
 */
export const DropGap = ({ parentPath, index }) => {
  const drag = useCriteriaDrag();
  const active = Boolean(drag?.isDragging && drag.canDropAt(parentPath, index));
  const { over, handlers } = useDropTarget(active, () => drag.moveTo(parentPath, index));
  if (!active) return null;

  return (
    <div {...handlers} data-testid={`gap:${pathKey(parentPath)}:${index}`} className="-my-1 flex h-4 items-center" aria-hidden>
      <span className={`h-0.5 w-full rounded-full transition-colors ${over ? 'bg-[#0062FB]' : 'bg-[#0062FB]/15'}`} />
    </div>
  );
};

/** Dropping onto a condition row pairs the two into a new group. */
export const PairDropTarget = ({ path, children }) => {
  const drag = useCriteriaDrag();
  const active = Boolean(drag?.isDragging && drag.canPairWith(path));
  const { over, handlers } = useDropTarget(active, () => drag.pairWith(path));

  return (
    <div {...handlers} data-testid={`pair:${pathKey(path)}`} className={over ? 'rounded-xl ring-2 ring-[#0062FB]' : ''}>
      {children}
      {over ? (
        <p className="px-2 pt-1 text-[11px] font-medium text-[#0062FB]">Drop to group these two together</p>
      ) : null}
    </div>
  );
};

/** Dropping onto a group's header moves the node inside that group. */
export const IntoGroupDropTarget = ({ groupPath, index, className = '', children }) => {
  const drag = useCriteriaDrag();
  const active = Boolean(drag?.isDragging && drag.canDropInto(groupPath));
  const { over, handlers } = useDropTarget(active, () => drag.moveTo(groupPath, index));

  return (
    <div {...handlers} className={`${className} ${over ? 'rounded-lg ring-2 ring-[#0062FB]' : ''}`}>
      {children}
      {over ? <p className="pt-1 text-[11px] font-medium text-[#0062FB]">Drop to move into this group</p> : null}
    </div>
  );
};
